import { useRef, useState } from 'react';
import { TranslationService } from '../../services/translation.service';
import SelectedFilterValue from '../Shared/SelectedFilterValue';
import LocationAutocomplete, { type ILocationAutocompleteSelection, type ILocationAutocompleteState } from '../Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../../models/location-type.enum';
import { type IAutocomplete } from '../Shared/AutocompleteBase';
import { AutocompleteLabelPosition } from '../Shared/AutocompleteBase/autocomplete-base.enum';
import CopyInput from './components/CopyInput';
import DepartureBoardLinkHelper from '../../helpers/departure-board-link.helper';
import ValidationInfo from '../../validation/validation-info';

interface ILocationViewModel {
  gid: string;
  nameWithoutMunicipality: string;
}

function DepartureBoardLinkGenerator() {
  // === STATE ===
  const [selectedStopAreas, setSelectedStopAreas] = useState<ILocationViewModel[]>([]);
  const [locationAutocompleteState, setLocationAutocompleteState] = useState<ILocationAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });

  // === REFS ===
  const locationAutocompleteRef = useRef<IAutocomplete>(null);

  const url =
    selectedStopAreas.length > 0
      ? DepartureBoardLinkHelper.generateLink(
          selectedStopAreas.length > 0 ? selectedStopAreas[0].gid : undefined,
          selectedStopAreas.length > 1 ? selectedStopAreas[1].gid : undefined,
          'vasttrafikse-depatureboardlinkgenerator'
        )
      : undefined;

  function handleLocationSelect(item: ILocationAutocompleteSelection | undefined): void {
    if (item === undefined || item.type !== LocationAutocompleteSelectionType.location) return;
    if (item.location && item.location.gid && item.location.name && !selectedStopAreas.some((x) => x.gid === item.location?.gid)) {
      setSelectedStopAreas([
        ...selectedStopAreas,
        {
          gid: item.location.gid,
          nameWithoutMunicipality: item.location.name.substring(0, item.location.name.indexOf(',')),
        },
      ]);
      setLocationAutocompleteState({
        value: undefined,
        validationInfo: new ValidationInfo(),
      });
    }
    locationAutocompleteRef.current?.clear();
  }

  function handleStopAreaRemove(id: string) {
    setSelectedStopAreas(selectedStopAreas.filter((x) => x.gid !== id));
  }

  function handleClearClick() {
    setSelectedStopAreas([]);
  }

  return (
    <div className="departure-board-link-generator">
      <div className="mb-2">
        <LocationAutocomplete
          /* @ts-expect-error ts(2322) */
          ref={locationAutocompleteRef}
          id="location-autocomplete"
          labelText={TranslationService.get('departureboardlinkgeneratorpage.stoplabel')}
          labelClassName="form-label"
          labelPosition={AutocompleteLabelPosition.above}
          placeholderText={TranslationService.get('departureboardlinkgeneratorpage.searchtitle')}
          disabled={selectedStopAreas.length > 1}
          listAriaLabelledBy="departure-board-link-generator-label"
          locationTypes={[LocationType.stopArea]}
          enableCurrentPosition={false}
          state={locationAutocompleteState}
          onSelect={handleLocationSelect}
        />
      </div>

      {selectedStopAreas.length > 0 && (
        <div className={selectedStopAreas.length > 0 ? 'mb-4' : ''}>
          <div className="w-100 d-flex flex-wrap" key="selected-filter-button">
            {selectedStopAreas.map((stopArea) => (
              <SelectedFilterValue key={stopArea.gid} ariaLabel={stopArea.nameWithoutMunicipality} id={stopArea.gid} children={stopArea.nameWithoutMunicipality} onRemove={handleStopAreaRemove} />
            ))}
            <button className="btn--as-link ms-2 btn-flush text-nowrap reset-btn" onClick={handleClearClick}>
              {TranslationService.get('departureboardlinkgeneratorpage.removefilter')}
            </button>
          </div>
        </div>
      )}

      {selectedStopAreas.length > 0 && (
        <div className="points-of-sale fullwidth-xs py-3 row">
          <div className="col px-sm-3">
            <CopyInput inputValue={url} inputId="ctic" />
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartureBoardLinkGenerator;
